<template>
    <transition name="chat-slide">
        <div v-show="isOpen" class="chat-window elevation-12">
            <!-- Header -->
            <div class="chat-header primary">
                <v-icon dark class="mr-2">{{ agentTakenOver ? 'mdi-face-agent' : 'mdi-robot' }}</v-icon>
                <span class="chat-header-title">{{ agentTakenOver ? 'Live Chat' : 'AI Assistant' }}</span>
                <v-spacer />
                <v-btn icon dark small @click="emitVisibility(false)">
                    <v-icon small>mdi-close</v-icon>
                </v-btn>
            </div>

            <!-- Messages -->
            <div ref="messagesContainer" class="chat-messages">
                <div v-if="messages.length === 0" class="chat-empty">
                    <v-icon size="48" color="grey lighten-1">mdi-robot-happy-outline</v-icon>
                    <p class="grey--text mt-2">How can I help you today?</p>
                </div>
                <div v-for="(msg, i) in messages" :key="i" class="chat-bubble-row"
                    :class="msg.role === 'customer' ? 'justify-end' : 'justify-start'">
                    <div class="chat-bubble-wrapper">
                        <span v-if="msg.name && msg.role !== 'customer'" class="chat-name-label">{{ msg.name }}</span>
                        <div class="chat-bubble"
                            :class="msg.role === 'customer' ? 'chat-bubble--user primary' : 'chat-bubble--assistant grey lighten-3'">
                            <span :class="[msg.role === 'customer' ? 'white--text' : '', 'chat-text']"
                                v-html="markdown(msg.text)"></span>
                        </div>
                    </div>
                </div>
                <div v-if="isLoading" class="chat-bubble-row justify-start">
                    <div class="chat-bubble chat-bubble--assistant grey lighten-3">
                        <v-progress-circular indeterminate size="16" width="2" color="primary" />
                    </div>
                </div>
            </div>

            <!-- Input -->
            <div class="chat-input-area">
                <v-text-field v-model="inputText" placeholder="Type a message..." hide-details dense outlined
                    class="chat-input" @keydown.enter="sendMessage">
                    <template #append>
                        <v-btn icon small color="primary" :disabled="!inputText.trim()" @click="sendMessage">
                            <v-icon>mdi-send</v-icon>
                        </v-btn>
                    </template>
                </v-text-field>
            </div>
        </div>
    </transition>
</template>

<script>
import SRConst from "@/helpers/Constants/SignalRConstants.js";
import AuthStoreMixin from "@/helpers/mixins/AuthStoreMixin";
import { marked } from "marked";

export default {
    name: 'AIAssistantChatWindow',
    mixins: [AuthStoreMixin],
    emits: ['update:modelValue', 'input'],
    mounted() {
        this.subscribeRealtime([
            SRConst.EventKeys.AI,
            this.notifyChanges
        ]);
        this.subscribeRealtime([
            SRConst.EventKeys.Chat,
            this.notifyChat
        ]);
    },
    props: {
        modelValue: {
            default: undefined
        },
        value: {
            default: undefined
        },
    },
    data() {
        return {
            inputText: '',
            messages: [],
            isLoading: false,
            agentTakenOver: false
        };
    },
    watch: {
        messages() {
            this.$nextTick(() => {
                this.scrollToBottom();
            });
        }
    },
    beforeUnmount() {
        this.disposeRealtimeSubscriptions();
    },
    methods: {
        emitVisibility(value) {
            this.$emit('update:modelValue', value);
            this.$emit('input', value);
        },
        disposeRealtimeSubscriptions() {
        this.unsubscribeRealtime([
            SRConst.EventKeys.AI,
            this.notifyChanges
        ]);
        this.unsubscribeRealtime([
            SRConst.EventKeys.Chat,
            this.notifyChat
        ]);
        },
        markdown(text) {
            return marked(text);
        },
        notifyChanges(args) {
            const content = args.response.content || {};
            const { text, role, name, messageId, tokenKind, delta } = content;

            // Legacy / non-streaming single-shot response
            if (!tokenKind) {
                this.messages.push({ role: role || 'assistant', name: name || null, text, messageId });
                this.isLoading = false;
                return;
            }

            // Only render text-bearing stream events in the main bubble
            if (tokenKind !== 'typing' && tokenKind !== 'final' && tokenKind !== 'error') {
                return;
            }

            const existing = messageId
                ? this.messages.find(m => m.messageId === messageId && m.role !== 'user' && m.role !== 'customer')
                : null;

            if (tokenKind === 'typing') {
                if (existing) {
                    existing.text = (existing.text || '') + (delta || '');
                } else {
                    this.messages.push({
                        role: role || 'assistant',
                        name: name || null,
                        text: delta || '',
                        messageId
                    });
                    this.messages = this.messages.slice();
                }
            } else {
                // final / error
                if (existing) {
                    if (text) existing.text = text;
                    if (tokenKind === 'final') existing.isFinal = true;
                } else {
                    this.messages.push({
                        role: role || 'assistant',
                        name: name || null,
                        text: text || '',
                        messageId,
                        isFinal: tokenKind === 'final'
                    });
                }
            }
            this.isLoading = false;
            this.$nextTick(() => this.scrollToBottom());
        },
        notifyChat(args) {
            this.agentTakenOver = true;
            const { text, role, name } = args.response.content;
            this.messages.push({ role: role || 'agent', name: name || null, text });
            this.isLoading = false;
        },
        sendMessage() {
            const text = this.inputText.trim();
            if (!text) return;

            this.inputText = '';

            if (this.agentTakenOver) {
                this.sendRealtimeMessage({
                    Mode: 'Chat',
                    Channel: `customer:${this.authUserInfo.Id}`,
                    Content: {
                        text
                    }
                });
                return;
            }

            this.messages.push({ role: 'customer', text });
            this.isLoading = true;
            this.sendRealtimeMessage({
                Mode: 'AI',
                Content: {
                    text
                }
            });
        },
        scrollToBottom() {
            const container = this.$refs.messagesContainer;
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        }
    },
    computed: {
        isOpen() {
            return this.modelValue ?? this.value ?? false;
        }
    }
};
</script>

<style scoped>
:deep(.chat-text p) {
    margin-bottom: 0px;
}

.chat-window {
    position: fixed;
    bottom: 80px;
    right: 24px;
    width: 380px;
    max-height: 540px;
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
    z-index: 999;
}

.chat-header {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    color: #fff;
    flex-shrink: 0;
}

.chat-header-title {
    font-size: 16px;
    font-weight: 600;
}

.chat-messages {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 16px;
    min-height: 300px;
    max-height: 380px;
}

.chat-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 260px;
}

.chat-bubble-row {
    display: flex;
    margin-bottom: 8px;
}

.chat-bubble {
    padding: 8px 14px;
    border-radius: 16px;
    font-size: 14px;
    line-height: 1.4;
    word-break: break-word;
}

.chat-bubble--user {
    border-bottom-right-radius: 4px;
}

.chat-bubble--assistant {
    border-bottom-left-radius: 4px;
}

.chat-bubble-wrapper {
    max-width: 80%;
    display: flex;
    flex-direction: column;
}

.chat-name-label {
    font-size: 11px;
    font-weight: 600;
    color: #757575;
    margin-bottom: 2px;
    margin-left: 8px;
}

.chat-input-area {
    padding: 10px 12px;
    border-top: 1px solid #e0e0e0;
    flex-shrink: 0;
}

.chat-input {
    font-size: 14px;
}

/* Slide transition */
.chat-slide-enter-active,
.chat-slide-leave-active {
    transition: all 0.25s ease;
}

.chat-slide-enter,
.chat-slide-leave-to {
    opacity: 0;
    transform: translateY(20px);
}
</style>
